import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import '@fontsource-variable/inter';
import '@fontsource/rozha-one/400.css';
import '@fontsource/saira-stencil-one/400.css';
import '@fontsource/courier-prime/400.css';
import '@fontsource/courier-prime/700.css';
import '@fontsource/courier-prime/400-italic.css';
import '@fontsource/vt323/400.css';
import { AuthProvider } from './auth/AuthProvider.js';
import { RequireAuth } from './auth/RequireAuth.js';
// Landing kept eager — it's the public root, must paint fast on cold load and
// has no PixiJS / heavy panel dependencies. Auth pages (login/signup) also
// stay eager so the unauthenticated entry flow has no Suspense flash.
import { LandingPage } from './landing/LandingPage.js';
import { LoginPage } from './auth/LoginPage.js';
import { SignupPage } from './auth/SignupPage.js';
import './index.css';

/**
 * G — Bundle split (perf audit).
 *
 * Every other route is `React.lazy()` so the cold-load main bundle no longer
 * carries the entire SPA. Biggest single win is `<App>` — it's the /play
 * route, which transitively imports all of PixiJS (Application, Container,
 * Sprite, etc.) plus every gameplay panel. Splitting it means a logged-out
 * visitor on the landing page or a player browsing /leaderboards never pays
 * for the canvas tree until they actually click "Play".
 *
 * Pre-split: a single ~1.04 MB index chunk (~293 KB gzip).
 * Post-split: vendor + landing + per-route chunks; the heaviest single
 * chunk should be the /play bundle which only loads when a player is
 * actively in the game.
 */
const App = lazy(() =>
  import('./App.js').then((m) => ({ default: m.App })),
);
const AdminPage = lazy(() =>
  import('./admin/AdminPage.js').then((m) => ({ default: m.AdminPage })),
);
const VerifyEmailPage = lazy(() =>
  import('./auth/VerifyEmailPage.js').then((m) => ({ default: m.VerifyEmailPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./auth/ResetPasswordPage.js').then((m) => ({ default: m.ResetPasswordPage })),
);
const ProfilePage = lazy(() =>
  import('./profile/ProfilePage.js').then((m) => ({ default: m.ProfilePage })),
);
const SpectatorMapPage = lazy(() =>
  import('./spectate/SpectatorMapPage.js').then((m) => ({ default: m.SpectatorMapPage })),
);
const SettingsPage = lazy(() =>
  import('./settings/SettingsPage.js').then((m) => ({ default: m.SettingsPage })),
);
const LeaderboardPage = lazy(() =>
  import('./leaderboards/LeaderboardPage.js').then((m) => ({ default: m.LeaderboardPage })),
);
const CorpsDirectoryPage = lazy(() =>
  import('./leaderboards/CorpsDirectoryPage.js').then((m) => ({ default: m.CorpsDirectoryPage })),
);
const ContractsPage = lazy(() =>
  import('./contracts/ContractsPage.js').then((m) => ({ default: m.ContractsPage })),
);
const GalaxyPage = lazy(() =>
  import('./galaxy/GalaxyPage.js').then((m) => ({ default: m.GalaxyPage })),
);
const FeedPage = lazy(() =>
  import('./feed/FeedPage.js').then((m) => ({ default: m.FeedPage })),
);
const TradesPage = lazy(() =>
  import('./trade/TradesPage.js').then((m) => ({ default: m.TradesPage })),
);
const MegaprojectsPage = lazy(() =>
  import('./megaprojects/MegaprojectsPage.js').then((m) => ({ default: m.MegaprojectsPage })),
);

const container = document.getElementById('root');
if (!container) throw new Error('#root element missing');

/**
 * Minimal Suspense fallback used while a lazy route chunk is fetched.
 * Matches the GateSplash style used by `<RequireAuth>` so both loading
 * states feel like the same UI.
 */
function RouteSplash() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-paperDeep">
      <span className="stencil text-[11px] tracking-capsWide text-brass/60">
        Loading…
      </span>
    </div>
  );
}

/**
 * Route tree. All routes are wrapped in `<AuthProvider>` so the session
 * hydration runs on every path. `<RequireAuth>` gates the main game and
 * the admin console — unauth'd visitors are sent to `/login`.
 *
 * P23 Batch F — `/` is now the public landing page. The game lives at
 * `/play/*` and is gated by `<RequireAuth>`. Successful login / signup
 * lands on `/play` rather than `/` so returning players go straight to
 * their charter; the landing page itself fast-forwards already-logged-
 * in visitors to `/play` so the marketing copy never blocks them.
 */
createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<RouteSplash />}>
          <Routes>
            {/* Public auth pages */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Public profile */}
            <Route path="/u/:corpSlug" element={<ProfilePage />} />
            <Route path="/u/:corpSlug/map" element={<SpectatorMapPage />} />

            {/* Public leaderboards + corps directory (P7A) */}
            <Route path="/leaderboards" element={<LeaderboardPage />} />
            <Route path="/corps" element={<CorpsDirectoryPage />} />

            {/* Admin console — requires authenticated admin role */}
            <Route
              path="/admin/*"
              element={
                <RequireAuth requireRole="admin">
                  <AdminPage />
                </RequireAuth>
              }
            />

            {/* Settings — requires authentication */}
            <Route
              path="/settings/*"
              element={
                <RequireAuth>
                  <SettingsPage />
                </RequireAuth>
              }
            />

            {/* P27 — Dedicated contracts + diplomacy page. Pulls
                ActiveContractsPanel / ContractBoard / BidBoard /
                FactionMatrix out of the Ops + Charter sidebar tabs into a
                wider layout where each section has room to breathe. */}
            <Route
              path="/contracts"
              element={
                <RequireAuth>
                  <ContractsPage />
                </RequireAuth>
              }
            />

            {/* P32 — Galactic Map: read-only discovery surface for "who
                is playing right now". Three drill-down levels in one
                page (factions → corps → public profile via /u/:slug). */}
            <Route
              path="/galaxy"
              element={
                <RequireAuth>
                  <GalaxyPage />
                </RequireAuth>
              }
            />

            {/* P35 — Activity Feed. Shows recent wins from the corps you
                follow (bid-board claims today; more emit-points planned). */}
            <Route
              path="/feed"
              element={
                <RequireAuth>
                  <FeedPage />
                </RequireAuth>
              }
            />

            {/* P36 — Player-to-player trade inbox + history. */}
            <Route
              path="/trades"
              element={
                <RequireAuth>
                  <TradesPage />
                </RequireAuth>
              }
            />

            {/* G1 — Cooperative megaprojects. */}
            <Route
              path="/megaprojects"
              element={
                <RequireAuth>
                  <MegaprojectsPage />
                </RequireAuth>
              }
            />

            {/* Public landing page — visible to logged-out visitors. The
                page itself fast-forwards already-authenticated users to
                `/play` so the marketing copy never blocks a returning
                player. */}
            <Route path="/" element={<LandingPage />} />

            {/* Main game — requires authentication. Moved off `/` so the
                landing page can own the public root. */}
            <Route
              path="/play/*"
              element={
                <RequireAuth>
                  <App />
                </RequireAuth>
              }
            />

            {/* Fallback — unknown routes drop back onto the landing page. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
